// HTMLWithAudioRenderer.js
import React, { useState, useRef, useEffect, useCallback } from 'react';

const HTMLWithAudioRenderer = ({ htmlContent, className = "" }) => {
  const [currentAudio, setCurrentAudio] = useState(null);
  const currentPlayingButtonRef = useRef(null);
  const containerRef = useRef(null);

  const resetAllButtons = useCallback(() => {
    console.log('Resetting all buttons...');
    if (!containerRef.current) return;
    
    const allButtons = containerRef.current.querySelectorAll('button.play-button[data-audio]');
    console.log('Found buttons to reset:', allButtons.length);
    
    allButtons.forEach((button, index) => {
      console.log(`Resetting button ${index + 1}:`, button);
      button.innerHTML = '▶';
      button.style.backgroundColor = 'rgb(59, 130, 246)';
    });
  }, []);

  const setButtonPlaying = useCallback((button) => {
    console.log('Setting button to playing state:', button);
    resetAllButtons();
    button.innerHTML = '⏸';
    button.style.backgroundColor = 'rgb(34, 197, 94)'; // Green when playing
    currentPlayingButtonRef.current = button;
    console.log('Button should now show pause icon');
  }, [resetAllButtons]);

  const setButtonStopped = useCallback((button) => {
    console.log('Setting button to stopped state:', button);
    if (button) {
      button.innerHTML = '▶';
      button.style.backgroundColor = 'rgb(59, 130, 246)';
    }
    currentPlayingButtonRef.current = null;
  }, []);

  const playAudio = useCallback((audioPath, clickedButton) => {
    console.log('=== PLAY AUDIO CALLED ===');
    console.log('Audio path:', audioPath);
    console.log('Clicked button:', clickedButton);
    console.log('Current playing button:', currentPlayingButtonRef.current);
    console.log('Current audio exists:', !!currentAudio);
    console.log('Button innerHTML before:', clickedButton.innerHTML);
    console.log('Are buttons the same?', currentPlayingButtonRef.current === clickedButton);
    
    // If same button clicked while playing, pause/stop
    if (currentAudio && currentPlayingButtonRef.current === clickedButton) {
      console.log('PAUSING: Same button clicked - stopping audio');
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setButtonStopped(clickedButton); // Use clickedButton instead of currentPlayingButtonRef.current
      setCurrentAudio(null);
      return;
    }
    
    // Stop current audio if playing
    if (currentAudio) {
      console.log('Stopping previous audio');
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setButtonStopped(currentPlayingButtonRef.current);
    }
    
    // Create new audio
    console.log('PLAYING: Creating new audio object');
    const newAudio = new Audio(audioPath);
    
    // Set button to playing state BEFORE playing
    console.log('About to set button to playing state');
    setButtonPlaying(clickedButton);
    console.log('Button innerHTML after setButtonPlaying:', clickedButton.innerHTML);
    
    newAudio.play().then(() => {
      console.log('Audio playing successfully');
      setCurrentAudio(newAudio);
    }).catch((error) => {
      console.error('Audio playback failed:', error);
      setButtonStopped(clickedButton);
      setCurrentAudio(null);
    });

    // Clean up when audio ends
    newAudio.addEventListener('ended', () => {
      console.log('Audio ended - resetting button');
      setButtonStopped(clickedButton);
      setCurrentAudio(null);
    });
  }, [currentAudio, setButtonPlaying, setButtonStopped]);

  // Add click event listeners to any existing audio buttons after component mounts/updates
  useEffect(() => {
    if (!containerRef.current) return;

    // Find all buttons with class 'play-button' and data-audio attribute
    const audioButtons = containerRef.current.querySelectorAll('button.play-button[data-audio]');
    
    console.log(`Found ${audioButtons.length} audio buttons`);

    const handleClick = (event) => {
      console.log('=== BUTTON CLICKED ===');
      console.log('Event:', event);
      console.log('This button:', event.target);
      console.log('Button innerHTML:', event.target.innerHTML);
      
      event.preventDefault();
      const audioPath = event.target.getAttribute('data-audio');
      console.log('Audio path from button:', audioPath);
      
      if (audioPath) {
        console.log('Calling playAudio with button reference');
        playAudio(audioPath, event.target);
      } else {
        console.error('No audio path found!');
      }
    };

    // Add event listeners to all audio buttons
    audioButtons.forEach((button, index) => {
      const audioPath = button.getAttribute('data-audio');
      console.log(`Button ${index + 1}:`, button, 'Audio path:', audioPath);
      console.log(`Button ${index + 1} innerHTML:`, button.innerHTML);
      button.addEventListener('click', handleClick);
    });

    // Cleanup function
    return () => {
      audioButtons.forEach(button => {
        button.removeEventListener('click', handleClick);
      });
    };
  }, [htmlContent, playAudio]); // Added playAudio to dependency array

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && currentAudio) {
        console.log('Escape pressed - stopping audio');
        currentAudio.pause();
        currentAudio.currentTime = 0;
        setButtonStopped(currentPlayingButtonRef.current);
        setCurrentAudio(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentAudio, setButtonStopped]);

  return (
    <div
      ref={containerRef}
      className={className}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

export default HTMLWithAudioRenderer;